import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PencilSimple, Trash, Eye, Heart, ChatCircle, Plus, ArrowSquareOut } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

export default function ArticlesList() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/articles/mine");
      setItems(data);
    } catch (e) { setErr("Failed to load articles"); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this article? This can't be undone.")) return;
    await api.delete(`/articles/${id}`);
    load();
  };

  return (
    <div className="pb-10">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-8">
        <div>
          <div className="eyebrow text-muted-foreground mb-2">Your work</div>
          <h1 className="font-serif text-4xl sm:text-5xl font-black tracking-tight">Articles</h1>
        </div>
        <div className="flex items-center gap-3">
          <Button asChild variant="outline" className="rounded-none border-foreground flex-1 sm:flex-none" data-testid="view-public-site-button">
            <Link to={`/${user.username}`} className="justify-center"><ArrowSquareOut size={14} className="mr-1.5" /> Site</Link>
          </Button>
          <Button asChild className="rounded-none bg-foreground text-background hover:bg-foreground/90 flex-1 sm:flex-none" data-testid="new-article-button">
            <Link to="/dashboard/new" className="justify-center"><Plus size={14} className="mr-1.5" /> New</Link>
          </Button>
        </div>
      </div>

      {err ? <div className="text-sm text-destructive">{err}</div> : null}
      {items === null ? (
        <div className="text-muted-foreground">Loading...</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-border p-12 text-center">
          <div className="font-serif text-2xl font-bold">No articles yet</div>
          <p className="mt-2 text-sm text-muted-foreground">Start your first essay — it'll appear at <span className="font-mono">/{user.username}</span></p>
          <Button asChild className="mt-6 rounded-none bg-foreground text-background hover:bg-foreground/90">
            <Link to="/dashboard/new"><Plus size={14} className="mr-1.5" /> Write your first piece</Link>
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {items.map((a) => (
            <div key={a.id} className="grid grid-cols-12 gap-4 border border-border bg-card p-4 lift-card" data-testid={`article-row-${a.id}`}>
              <div className="col-span-12 md:col-span-3">
                {a.cover_image ? (
                  <img src={fileUrl(a.cover_image)} alt="" className="w-full h-32 object-cover" />
                ) : (
                  <div className="w-full h-32 bg-secondary grid place-items-center text-muted-foreground text-xs eyebrow">No cover</div>
                )}
              </div>
              <div className="col-span-12 md:col-span-6">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 eyebrow text-muted-foreground text-[10px]">
                  <span>{new Date(a.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</span>
                  <span className="hidden xs:inline">·</span>
                  {a.is_scheduled ? (
                    <span className="text-blue-700 dark:text-blue-400">Scheduled · {new Date(a.publish_at).toLocaleString()}</span>
                  ) : a.published ? (
                    <span className="text-emerald-700 dark:text-emerald-400">Published</span>
                  ) : (
                    <span className="text-amber-700 dark:text-amber-400">Draft</span>
                  )}
                  {a.category && (
                    <>
                      <span className="hidden xs:inline">·</span>
                      <span className="bg-orange-600/10 text-orange-600 px-2 py-0.5 font-black uppercase tracking-widest">{a.category.name}</span>
                    </>
                  )}
                  {a.sub_category && (
                    <>
                      <span className="hidden xs:inline">·</span>
                      <span className="italic">{a.sub_category}</span>
                    </>
                  )}
                </div>
                <h3 className="font-serif text-2xl font-bold tracking-tight mt-1">{a.title}</h3>
                <p className="mt-1 text-sm text-muted-foreground line-clamp-2">{a.excerpt}</p>
                <div className="mt-3 flex items-center gap-5 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><Eye size={14} /> {a.views}</span>
                  <span className="flex items-center gap-1"><Heart size={14} /> {a.likes_count}</span>
                  <span className="flex items-center gap-1"><ChatCircle size={14} /> {a.comments_count}</span>
                </div>
              </div>
              <div className="col-span-12 md:col-span-3 flex md:justify-end items-start gap-2">
                <Button asChild variant="outline" className="rounded-none" data-testid={`edit-article-${a.id}`}>
                  <Link to={`/dashboard/edit/${a.id}`}><PencilSimple size={14} className="mr-1.5" /> Edit</Link>
                </Button>
                <Button variant="outline" className="rounded-none text-destructive border-destructive hover:bg-destructive hover:text-destructive-foreground" onClick={() => remove(a.id)} data-testid={`delete-article-${a.id}`}>
                  <Trash size={14} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
